import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import LoadingSpinner from "../LoadingSpinner";
import MapComponent from "../maps/MapComponent";
import { MapPin, User } from "react-feather";
import Input from "../input/Input";
import Dropdown from "../input/Dropdown";
import { FORMS, MAP_TYPES, MODULES } from "../../const";
import { locationValidation } from "../validations/locationCreation";
import InfoList from "../InfoList";
import { formatFromJsonDate } from "../../dateConfig";
import useUserCompany from "../../hooks/useUserCompany";
import SearchDropdown from "../input/SearchDropdown";
import {
  getOtherRefSearchDropdown,
  getFieldValuesByTableAndId,
} from "../../api/keyvalue";

const FORM = FORMS.LOCATION.LOC_DETAIL.form;

const LocationForm = forwardRef(
  (
    {
      errors,
      isLoading,
      dropdown,
      currentValues,
      editMode,
      setCurrentLocationData,
    },
    ref,
  ) => {
    const userCompany = useUserCompany();
    const [initialActive, setInitialActive] = useState(
              editMode ? currentValues?.locActive : 1,
            );

    const [company, setCompany] = useState(
      userCompany ? userCompany.comId : null,
    );
    const [locationData, setLocationData] = useState(
      editMode
        ? currentValues
        : {
            locCode: "",
            locComId: company,
            locType: "",
            locReference: "",
            locReference2: "",
            locName: "",
            locFenceName: "",
            locDescription: "",
            locLon: "",
            locLat: "",
            locIcon: "",
            iocColor: "",
            locStatus: 1,
            locActive: 1,
            locShippers: [],
            locShippersMap: {},
          },
    );

    useEffect(() => {
      setCompany(userCompany ? userCompany.comId : null);
    }, [userCompany]);

    const [currentErrors, setCurrentErrors] = useState({});

    useEffect(() => {
      setCurrentErrors(errors);
    }, [errors]);

    useEffect(() => {
      (async () => {
        if (!editMode || !currentValues?.location?.locShippers) return;
        const ids = currentValues.location.locShippers;
        if (!ids.length) {
          setLocationData((prev) => ({
            ...prev,
            locShippers: [],
            locShippersMap: {},
          }));
          return;
        }
        const results = await Promise.all(
          ids.map((id) =>
            getFieldValuesByTableAndId({
              tableName: "masterdata.tms_m_stakeholder",
              primaryKey: "stk_id",
              fieldNames: "stk_name",
              ids: id.toString(),
            }),
          ),
        );
        const map = {};
        results.forEach((rows, i) => {
          map[ids[i]] = rows?.[0]?.field1 ?? String(ids[i]);
        });
        setLocationData((prev) => ({
          ...prev,
          locShippers: ids,
          locShippersMap: map,
        }));
      })();
    }, [editMode, currentValues]);

    const [showLocationPicker, setShowLocationPicker] = useState(false);

    useImperativeHandle(ref, () => ({
      getData: () => locationData,

       isDeletedActive: () =>
        locationData.locActive === 2 && initialActive !== 2,
      resetForm: () => {
        setLocationData({
          locCode: "",
          locType: "",
          locReference: "",
          locReference2: "",
          locName: "",
          locFenceName: "",
          locDescription: "",
          locLon: "",
          locLat: "",
          locIcon: "",
          iocColor: "",
          locStatus: 1,
          locActive: 1,
          locShippers: [],
          locShippersMap: {},
        });
        setCurrentErrors({});
        setLocationData("");
        setSearchFieldReset(true);
      },
      validateForm: () => {
        return locationValidation(locationData);
      },
    }));
    const [searchFieldReset, setSearchFieldReset] = useState(false);

    const handleLocationPicker = (e) => {
      e.preventDefault();
      setShowLocationPicker(true);
    };
    const handleCloseLocationDetails = (e) => {
      e.preventDefault();
      setShowLocationPicker(false);
    };
    const handleLocationSelect = (newLocation) => {
      setLocationData((prevState) => ({
        ...prevState,
        locLat: newLocation.lat.toString(),
        locLon: newLocation.lng.toString(),
      }));
      setShowLocationPicker(false); // Close the modal after setting the location
    };

    const handleClose = (e) => {
      setShowLocationPicker(false);
    };
    return (
      <>
        {isLoading ? (
          <LoadingSpinner />
        ) : (
          <form className="ui form">
            <div className="ui segment">
              <h4 className="ui dividing header">Location Creation</h4>
              <div className="field">
                <Input
                  label="Location Code"
                  error={currentErrors.locCode}
                  module={MODULES.Master}
                  form={FORM}
                  field="loc_code"
                  editMode={editMode}
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { locCode: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                  name="locCode"
                  placeholder="Location Code"
                  value={locationData.locCode}
                  onChange={(e) => {
                    setLocationData((prevState) => ({
                      ...prevState,
                      locCode: e.target.value,
                    }));
                  }}
                  disabled
                />
              </div>
              <div className="two fields">
                <Dropdown
                  required={true}
                  label="Type"
                  error={currentErrors.locType}
                  module={MODULES.Master}
                  form={FORM}
                  field="loc_type"
                  editMode={editMode}
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { locType: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                  options={dropdown.locTypeOptions}
                  style={{ height: "20px" }}
                  placeholder="Type"
                  value={locationData.locType}
                  onChange={(e, data) =>
                    setLocationData((prevState) => ({
                      ...prevState,
                      locType: data.value,
                    }))
                  }
                />
                <Input
                  required={true}
                  label="Location Name"
                  error={currentErrors.locName}
                  module={MODULES.Master}
                  form={FORM}
                  field="loc_name"
                  editMode={editMode}
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { locName: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                  name="locName"
                  value={locationData.locName}
                  onChange={(e) => {
                    setLocationData((prevState) => ({
                      ...prevState,
                      locName: e.target.value,
                    }));
                    setCurrentLocationData(e.target.value);
                  }}
                  duplicate={false}
                  // Newly added for company wise duplicate validation.
                  activeCol="loc_active"
                  companyCol="loc_com_id"
                  companyWise={true}
                  //
                  tableName="masterdata.tms_m_locations"
                  primaryKey="loc_id"
                  searchField="loc_name"
                  keyType="S"
                  duplicateErrorMessage="This Location name already exists"
                  setErrors={setCurrentErrors}
                  errorField="locName"
                  id={locationData.locId}
                />
              </div>
              <div className="two fields">
                <Input
                  label="Reference 1"
                  error={currentErrors.locReference}
                  module={MODULES.Master}
                  form={FORM}
                  field="loc_reference"
                  editMode={editMode}
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { locReference: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                  name="locReference"
                  value={locationData.locReference}
                  onChange={(e) => {
                    setLocationData((prevState) => ({
                      ...prevState,
                      locReference: e.target.value,
                    }));
                  }}
                />
                <Input
                  label="Reference 2"
                  error={currentErrors.locReference2}
                  module={MODULES.Master}
                  form={FORM}
                  field="loc_reference2"
                  editMode={editMode}
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { locReference2: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                  name="locReference2"
                  value={locationData.locReference2}
                  onChange={(e) => {
                    setLocationData((prevState) => ({
                      ...prevState,
                      locReference2: e.target.value,
                    }));
                  }}
                />
              </div>
              <div className="two fields">
                <Input
                  label="Description"
                  error={currentErrors.locDescription}
                  module={MODULES.Master}
                  form={FORM}
                  field="loc_description"
                  editMode={editMode}
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { locDescription: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                  name="locDescription"
                  value={locationData.locDescription}
                  onChange={(e) => {
                    setLocationData((prevState) => ({
                      ...prevState,
                      locDescription: e.target.value,
                    }));
                  }}
                />
                <Input
                  label="Fence Name"
                  error={currentErrors.locFenceName}
                  module={MODULES.Master}
                  form={FORM}
                  field="loc_fence_name"
                  editMode={editMode}
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { locFenceName: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                  name="locFenceName"
                  value={locationData.locFenceName}
                  onChange={(e) => {
                    setLocationData((prevState) => ({
                      ...prevState,
                      locFenceName: e.target.value,
                    }));
                  }}
                />
              </div>
              <div className="two fields">
                <Input
                  label="Icon"
                  error={currentErrors.locIcon}
                  module={MODULES.Master}
                  form={FORM}
                  field="loc_icon"
                  editMode={editMode}
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { locIcon: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                  name="locIcon"
                  value={locationData.locIcon}
                  onChange={(e) => {
                    setLocationData((prevState) => ({
                      ...prevState,
                      locIcon: e.target.value,
                    }));
                  }}
                />
                <Input
                  label="Color"
                  error={currentErrors.iocColor}
                  module={MODULES.Master}
                  form={FORM}
                  field="ioc_color"
                  editMode={editMode}
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { iocColor: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                  name="iocColor"
                  value={locationData.iocColor}
                  onChange={(e) => {
                    setLocationData((prevState) => ({
                      ...prevState,
                      iocColor: e.target.value,
                    }));
                  }}
                />
              </div>
              <div
                className="two fields"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Input
                  required={true}
                  label="Latitude"
                  error={currentErrors.locLat}
                  module={MODULES.Master}
                  form={FORM}
                  field="loc_lat"
                  editMode={editMode}
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { locLat: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                  name="locLat"
                  value={locationData.locLat}
                  onChange={(e) => {
                    setLocationData((prevState) => ({
                      ...prevState,
                      locLat: e.target.value,
                    }));
                  }}
                />
                <Input
                  required={true}
                  label="Longitude"
                  error={currentErrors.locLon}
                  module={MODULES.Master}
                  form={FORM}
                  field="loc_lon"
                  editMode={editMode}
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { locLon: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                  name="locLon"
                  value={locationData.locLon}
                  onChange={(e) => {
                    setLocationData((prevState) => ({
                      ...prevState,
                      locLon: e.target.value,
                    }));
                  }}
                />
                <button
                  className="ui primary button"
                  onClick={(e) => handleLocationPicker(e)}
                  style={{
                    marginTop: "1.9rem",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: "bold",
                  }}
                >
                  <MapPin size={15} />
                </button>
              </div>
              <div className="field">
                <SearchDropdown
                  key={1}
                  label="Plant / Shipper"
                  multiGrid={true}
                  editMode={editMode}
                  module={MODULES.Master}
                  form={FORM}
                  field="loc_shippers"
                  reset={searchFieldReset}
                  setReset={setSearchFieldReset}
                  clearable={true}
                  multiple
                  returnFullObject
                  multiValueTwoWayBinding={true}
                  multiValues={locationData.locShippersMap}
                  multiValueRemoveHandler={(id) => {
                    setLocationData((prev) => {
                      const removeId = Number(id);
                      const nextIds = prev.locShippers
                        .map(Number)
                        .filter((x) => x !== removeId);

                      const { [String(id)]: _, ...rest } = prev.locShippersMap;

                      return {
                        ...prev,
                        locShippers: nextIds,
                        locShippersMap: rest,
                      };
                    });
                  }}
                  onResultSelect={(item) => {
                    setLocationData((prev) => {
                      const id = Number(item.value);
                      if (prev.locShippers.map(Number).includes(id))
                        return prev;
                      return {
                        ...prev,
                        locShippers: [...prev.locShippers.map(Number), id],
                        locShippersMap: {
                          ...prev.locShippersMap,
                          [String(id)]: item.title,
                        },
                      };
                    });
                  }}
                  api={getOtherRefSearchDropdown}
                  apiDependency={{ type: "stakeholder_type", value: "Shipper" }}
                  fieldInfo={{
                    tableName: "masterdata.tms_m_stakeholder",
                    primaryKey: "stk_id",
                    fieldName: "stk_name",
                    id: undefined,
                  }}
                />
              </div>

              <div className="two fields">
                <Dropdown
                  required={true}
                  label="Active"
                  error={currentErrors.locActive}
                  module={MODULES.Master}
                  form={FORM}
                  field="loc_active"
                  authenticated={true}
                  needDeletePermission={true}
                  editMode={editMode}
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { locActive: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                  options={dropdown.activeOptions}
                  style={{ height: "20px" }}
                  placeholder="Active"
                  value={locationData.locActive}
                  onChange={(e, data) =>
                    setLocationData((prevState) => ({
                      ...prevState,
                      locActive: data.value,
                    }))
                  }
                />
                <Dropdown
                  required={true}
                  label="Status"
                  error={currentErrors.locStatus}
                  module={MODULES.Master}
                  form={FORM}
                  field="LOCATION_STATUS"
                  authenticated={true}
                  editMode={editMode}
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { locStatus: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                  options={dropdown.statusOptions}
                  style={{ height: "20px" }}
                  placeholder="Status"
                  value={locationData.locStatus}
                  onChange={(e, data) =>
                    setLocationData((prevState) => ({
                      ...prevState,
                      locStatus: data.value,
                    }))
                  }
                />
              </div>
              {showLocationPicker && (
                <div className="ui dimmer modals visible active">
                  <div
                    className="ui standard modal visible active"
                    style={{ width: "80%", height: "auto" }}
                  >
                    <div className="content">
                      <MapComponent
                        type={MAP_TYPES.PICK}
                        onLocationSelect={handleLocationSelect}
                        pickLocation={
                          locationData.locLon && locationData.locLon
                            ? {
                                lat: parseFloat(locationData.locLat),
                                lng: parseFloat(locationData.locLon),
                              }
                            : { lat: 6.9271, lng: 79.8612 }
                        }
                        handleClose={handleClose}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
            {editMode && (
              <InfoList
                title="User Information"
                content={[
                  {
                    title: "Created By",
                    value: locationData.createdBy
                      ? locationData.createdBy
                      : "N/A",
                  },
                  {
                    title: "Created Date",
                    value: locationData.createdDate
                      ? formatFromJsonDate(locationData.createdDate)
                      : "N/A",
                  },
                  {
                    title: "Last Modified By",
                    value: locationData.lastModifiedBy
                      ? locationData.lastModifiedBy
                      : "N/A",
                  },
                  {
                    title: "Last Modified Date",
                    value: locationData.lastModifiedDate
                      ? formatFromJsonDate(locationData.lastModifiedDate)
                      : "N/A",
                  },
                ]}
                Icon={User}
              />
            )}
          </form>
        )}
      </>
    );
  },
);

export default LocationForm;
